<template>
  <div v-if="!disabled" class="upload-file">
    <el-upload ref="uploadRef" v-model:file-list="fileList" :action="uploadUrl" :auto-upload="autoUpload"
      :before-upload="beforeUpload" :disabled="disabled" :drag="drag" :http-request="httpRequest" :limit="props.limit"
      :multiple="props.limit > 1" :on-error="excelUploadError" :on-exceed="handleExceed" :on-preview="handlePreview"
      :on-remove="handleRemove" :on-success="handleFileSuccess" :show-file-list="true" class="upload-file-uploader"
      :accept="acceptString" name="file">
      <el-button type="primary">
        <Icon icon="ep:upload-filled" />
        选取文件
      </el-button>
      <template v-if="isShowTip" #tip>
        <div style="font-size: 12px;color: #888; margin-top: 10px;">
          大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b>
        </div>
        <div style="font-size: 12px;color: #888; margin-top: 4px;">
          格式为 <b style="color: #f56c6c">{{ fileType.join('/') }}</b> 的文件
        </div>
      </template>
      <template #file="row">
        <div class="flex items-center">
          <span>{{ row.file.name }}</span>
          <div class="ml-10px">
            <el-link :underline="false" type="primary" @click="handleDownloadFile(row.file.url)">
              下载
            </el-link>
          </div>
          <div class="ml-10px">
            <el-button link type="danger" @click="handleRemove(row.file)"> 删除</el-button>
          </div>
        </div>
      </template>
    </el-upload>
  </div>

  <!-- 上传操作禁用时 -->
  <div v-if="disabled" class="upload-file">
    <div v-for="(file, index) in fileList" :key="index" class="flex items-center file-list-item">
      <span>{{ file.name }}</span>
      <div class="ml-10px">
        <el-link :underline="false" type="primary" @click="handleDownloadFile(file.url)">
          下载
        </el-link>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { nextTick } from 'vue'
import { propTypes } from '@/utils/propTypes'
import type { UploadInstance, UploadProps, UploadRawFile, UploadUserFile, UploadFile } from 'element-plus'
import { isString } from '@/utils/is'
import { useUpload } from '@/components/UploadFile/src/useUpload'
import { getSignedUrl } from '@/utils/cosAuth'
import download from '@/utils/download'
import { getFileNameFromUrl } from '@/utils/file'

defineOptions({ name: 'UploadFile' })

const message = useMessage() // 消息弹窗
const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: propTypes.oneOfType<string | string[]>([String, Array<String>]).isRequired,
  fileType: propTypes.array.def(['doc', 'xls', 'ppt', 'txt', 'pdf']), // 文件类型, 例如['png', 'jpg', 'jpeg']
  fileSize: propTypes.number.def(5), // 大小限制(MB)
  limit: propTypes.number.def(5), // 数量限制
  autoUpload: propTypes.bool.def(true), // 自动上传
  drag: propTypes.bool.def(false), // 拖拽上传
  isShowTip: propTypes.bool.def(true), // 是否显示提示
  disabled: propTypes.bool.def(false), // 是否禁用上传组件 ==> 非必传（默认为 false）
  directory: propTypes.string.def(undefined) // 上传目录 ==> 非必传（默认为 undefined）
})

// ========== 上传相关 ==========
const uploadRef = ref<UploadInstance>()
const uploadList = ref<UploadUserFile[]>([])
const fileList = ref<UploadUserFile[]>([])
const uploadNumber = ref<number>(0)

const { uploadUrl, httpRequest } = useUpload(props.directory)

// 根据 fileType 动态生成 accept 字符串（系统文件选择器预过滤）
// 注意：accept 只是浏览器建议，不是强制限制（用户可在文件对话框选"全部"绕过）
// beforeUpload 仍作为最后兜底校验
const acceptString = computed(() => props.fileType.map((t) => `.${t}`).join(','))

// 文件上传之前判断
const beforeUpload: UploadProps['beforeUpload'] = (file: UploadRawFile) => {
  if (fileList.value.length >= props.limit) {
    message.error(`上传文件数量不能超过${props.limit}个!`)
    return false
  }
  let fileExtension = ''
  if (file.name.lastIndexOf('.') > -1) {
    fileExtension = file.name.slice(file.name.lastIndexOf('.') + 1)
  }
  const isImg = props.fileType.some((type: string) => {
    if (file.type.indexOf(type) > -1) return true
    return !!(fileExtension && fileExtension.indexOf(type) > -1)
  })
  const isLimit = file.size < props.fileSize * 1024 * 1024
  if (!isImg) {
    message.error(`文件格式不正确, 请上传${props.fileType.join('/')}格式!`)
    return false
  }
  if (!isLimit) {
    message.error(`上传文件大小不能超过${props.fileSize}MB!`)
    return false
  }
  message.success('正在上传文件，请稍候...')
  // 只有在验证通过后才增加计数器
  uploadNumber.value++
  return true
}
// 处理上传的文件发生变化
// const handleFileChange = (uploadFile: UploadFile): void => {
//   uploadRef.value.data.path = uploadFile.name
// }
// 文件上传成功
const handleFileSuccess: UploadProps['onSuccess'] = (res, uploadFile) => {
  message.success('上传成功')
  // 删除自身
  const resData = (res as any).data
  const index = fileList.value.findIndex((item) => (item.response as any)?.data === resData)
  fileList.value.splice(index, 1)
  // 修复文件名乱码：用浏览器 File 对象的原始 name，而不是 res.data（URL 路径里的 UUID 或 URL 编码片段）
  uploadList.value.push({ name: uploadFile.name, url: resData })
  if (uploadList.value.length == uploadNumber.value) {
    fileList.value.push(...uploadList.value)
    uploadList.value = []
    uploadNumber.value = 0
    emitUpdateModelValue()
  }
}
// 文件数超出提示
const handleExceed: UploadProps['onExceed'] = (): void => {
  message.error(`上传文件数量不能超过${props.limit}个!`)
}
// 上传错误提示
const excelUploadError: UploadProps['onError'] = (): void => {
  message.error('导入数据失败，请您重新上传！')
  // 上传失败时减少计数器，避免后续上传被阻塞
  uploadNumber.value = Math.max(0, uploadNumber.value - 1)
}
// 删除上传文件
const handleRemove = (file: UploadFile) => {
  const index = fileList.value.map((f) => f.name).indexOf(file.name)
  if (index > -1) {
    fileList.value.splice(index, 1)
    emitUpdateModelValue()
  }
}
const handlePreview: UploadProps['onPreview'] = (uploadFile) => {
  console.log(uploadFile)
}

// 下载文件（通过签名 URL）
const handleDownloadFile = async (fileUrl?: string) => {
  if (!fileUrl) return
  try {
    const signedUrl = await getSignedUrl(fileUrl)
    const fileName = getFileNameFromUrl(fileUrl)
    await download.fromUrl(signedUrl, fileName)
  } catch (error) {
    console.error('下载文件失败:', error)
    message.error('下载失败，请稍后重试')
  }
}

// 内部更新标志：上传成功/删除 emit 时设为 true，让下面的 watch 跳过；下一个微任务还原
// 注意：必须声明在 watch 之前，immediate:true 会立刻执行回调，否则 TDZ 报错
let isInternalUpdate = false
// 监听模型绑定值变动
watch(
  () => props.modelValue,
  (val: string | string[]) => {
    // 内部 emit（上传成功/删除）时跳过，避免用 URL 解码覆盖刚用 file.name 设置的正确文件名
    if (isInternalUpdate) return
    if (!val) {
      fileList.value = [] // fix：处理掉缓存，表单重置后上传组件的内容并没有重置
      return
    }
    fileList.value = [] // 保障数据为空
    // 情况1：字符串
    if (isString(val)) {
      // 修复文件名乱码：用 getFileNameFromUrl 替代 substring，自动 decodeURIComponent + 去掉查询参数
      fileList.value.push(
        ...val.split(',').map((url) => ({ name: getFileNameFromUrl(url), url }))
      )
      return
    }
    // 情况2：数组
    fileList.value.push(
      ...(val as string[]).map((url) => ({ name: getFileNameFromUrl(url), url }))
    )
  },
  { immediate: true, deep: true }
)
// 发送文件链接列表更新
const emitUpdateModelValue = () => {
  // 情况1：数组结果
  let result: string | string[] = fileList.value.map((file) => file.url!)
  // 情况2：逗号分隔的字符串
  if (props.limit === 1 || isString(props.modelValue)) {
    result = result.join(',')
  }
  isInternalUpdate = true
  emit('update:modelValue', result)
  nextTick(() => {
    isInternalUpdate = false
  })
}
</script>
<style lang="scss" scoped>
.upload-file-uploader {
  margin-bottom: 5px;
}

:deep(.upload-file-list .el-upload-list__item) {
  position: relative;
  margin-bottom: 10px;
  line-height: 2;
  border: 1px solid #e4e7ed;
}

:deep(.el-upload-list__item-file-name) {
  max-width: 250px;
}

:deep(.upload-file-list .ele-upload-list__item-content) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: inherit;
}

:deep(.ele-upload-list__item-content-action .el-link) {
  margin-right: 10px;
}

.file-list-item {
  border: 1px dashed var(--el-border-color-darker);
  border-radius: 8px;
}
</style>
